import { useState, useEffect, useMemo } from 'react';
import { reportApi } from '../../api/report.api';
import { PageHeader, Skeleton, EmptyState, Badge, Spinner, Btn } from '../../components/ui/index';
import { formatPersianDateLong } from '../../utils/persianNumber';
import { toGregorianDateParams, startOfDay } from '../../utils/jalali';
import JalaliDatePicker from '../../components/calendar/JalaliDatePicker';
import { parseDailyReportItemContent, invoiceCostLabel } from '../../utils/dailyReportItemCodec';
import { formatEstimatedMinutes, DEFAULT_ESTIMATED_TIME } from '../../components/ui/EstimatedTimeSlider';
import DailyReportPrintForm from '../../components/reports/DailyReportPrintForm';
import { BASE_MEDIA_URL } from '../../api/client';
import { useAuth } from '../../store/AuthContext';
import { getDailyReportApprovalDisplay, normalizeManagerDailyReportApproval } from '../../utils/dailyReportApproval';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import { ArrowLeft, Tray } from '@phosphor-icons/react';
import { useCompanyDisplay } from '../../utils/companyDisplay';

const filterBtnStyle = (active) => ({
  padding: '7px 14px',
  borderRadius: 20,
  border: `1.5px solid ${active ? '#29AAE1' : '#e2e8f0'}`,
  background: active ? '#EDF3FC' : '#fff',
  color: active ? '#233A7A' : '#64748b',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
});

export default function ManagerDailyReportPage() {
  const companyLabel = useCompanyDisplay();
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected] = useState(null);
  const [report, setReport] = useState(null);
  const [approval, setApproval] = useState(null);
  const [employeeMeta, setEmployeeMeta] = useState(null);
  const [deptParts, setDeptParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reportLoading, setReportLoading] = useState(false);
  const [approving, setApproving] = useState(false);
  const [date, setDate] = useState(() => startOfDay(new Date()));
  const [search, setSearch] = useState('');

  useEffect(() => {
    reportApi.managerGetDepartmentEmployee()
      .then(r => setEmployees(Array.isArray(r.data) ? r.data : []))
      .catch(() => {})
      .finally(() => setLoading(false));
    reportApi.getDailyReportItems()
      .then(r => setDeptParts(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, []);

  useAutoRefresh(() => { if (selected) loadReport(selected, date, false); }, { paused: approving });

  const filteredEmployees = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter(u => String(u).toLowerCase().includes(q));
  }, [employees, search]);

  const loadReport = (username, reportDate = date, showSpinner = true) => {
    setSelected(username);
    if (showSpinner) {
      setReport(null);
      setApproval(null);
      setEmployeeMeta(null);
      setReportLoading(true);
    }
    const { year, month, day } = toGregorianDateParams(reportDate);
    reportApi.managerGetDailyReport({
      employee_username: username,
      'date[year]': year,
      'date[month]': month,
      'date[day]': day,
    })
      .then(r => {
        const data = r.data;
        if (Array.isArray(data)) {
          setReport(data);
          setApproval(null);
          setEmployeeMeta(null);
        } else {
          setReport(Array.isArray(data?.items) ? data.items : []);
          setApproval(normalizeManagerDailyReportApproval(data?.approval));
          setEmployeeMeta({
            employeeName: data?.employee_name || username,
            departmentName: data?.employee_department_name || user?.department_name || '',
            employeeSignatureFile: data?.employee_signature_file || null,
          });
        }
      })
      .catch(() => {
        setReport([]);
        setApproval(null);
      })
      .finally(() => setReportLoading(false));
  };

  const handleApprove = async () => {
    if (!selected) return;
    setApproving(true);
    const { year, month, day } = toGregorianDateParams(date);
    try {
      const res = await reportApi.approveDailyReport({
        employee_username: selected,
        'date[year]': year,
        'date[month]': month,
        'date[day]': day,
      });
      setApproval(normalizeManagerDailyReportApproval(res.data?.approval) || { approved: true, submitted: true });
      loadReport(selected, date);
    } catch {
      // silent — manager can retry
    } finally {
      setApproving(false);
    }
  };

  useEffect(() => {
    if (selected) loadReport(selected, date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  const parsedItems = useMemo(() => {
    if (!report) return [];
    return report.map(item => ({
      id: item.report_id,
      ...parseDailyReportItemContent(item.content || ''),
    }));
  }, [report]);

  const dateLabel = formatPersianDateLong(date);
  const approvalDisplay = getDailyReportApprovalDisplay(approval);
  const partLabel = (partId) => {
    if (partId == null) return '—';
    if (String(partId) === 'builtin-tickets-missions') return 'تیکت‌ها و ماموریت';
    if (String(partId) === 'builtin-other') return 'سایر';
    const part = deptParts.find(p => p.item_id === partId || String(p.item_id) === String(partId));
    return part?.name || '—';
  };
  const printParts = [
    {
      item_id: 'builtin-tickets-missions',
      name: 'تیکت‌ها و ماموریت',
      need_cost: false,
      need_company_name: false,
      need_phone_number: false,
      need_full_name: false,
    },
    {
      item_id: 'builtin-other',
      name: 'سایر',
      need_cost: false,
      need_company_name: false,
      need_phone_number: false,
      need_full_name: false,
    },
    ...deptParts,
  ];
  const managerSignatureUrl = approval?.manager_signature_file
    ? `${BASE_MEDIA_URL}${approval.manager_signature_file}`
    : null;
  const expertSignatureUrl = employeeMeta?.employeeSignatureFile
    ? `${BASE_MEDIA_URL}${employeeMeta.employeeSignatureFile}`
    : null;

  const handlePrint = () => window.print();

  return (
    <div className="fade-in" style={{ minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
      {selected && (
        <DailyReportPrintForm
          dateLabel={dateLabel}
          employeeName={employeeMeta?.employeeName || selected}
          departmentName={employeeMeta?.departmentName || user?.department_name || ''}
          items={parsedItems}
          parts={printParts}
          partLabel={partLabel}
          expertSignatureUrl={expertSignatureUrl}
          managerSignatureUrl={managerSignatureUrl}
          managerName={approval?.approved_by_name}
          approvalStatusLabel={approvalDisplay?.label}
          approvedAt={approval?.approved_at}
        />
      )}
      <PageHeader
        title="گزارش روزانه کارمندان"
        subtitle={selected ? `گزارش ${selected} — ${dateLabel}` : dateLabel}
      />

      <div style={{ display: 'flex', gap: 10, marginBottom: 20, alignItems: 'center', flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#64748b', fontWeight: 600 }}>
          تاریخ:
          <JalaliDatePicker value={date} onChange={setDate} />
        </label>
        <input
          type="search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="جستجوی کارمند..."
          style={{
            flex: '1 1 180px',
            minWidth: 0,
            maxWidth: 320,
            padding: '8px 12px',
            border: '1.5px solid #e2e8f0',
            borderRadius: 8,
            fontSize: 13,
            outline: 'none',
            fontFamily: 'inherit',
          }}
        />
        {selected && (
          <button type="button" onClick={() => loadReport(selected)} style={filterBtnStyle(false)}>
            🔄 بارگذاری مجدد
          </button>
        )}
      </div>

      <div className="pj-manager-report-grid">
        {/* Employee sidebar */}
        <div style={{
          background: '#fff',
          borderRadius: 14,
          border: '1px solid rgba(35,58,122,0.1)',
          overflow: 'hidden',
          boxShadow: '0 4px 16px rgba(35,58,122,0.08)',
        }}>
          <div style={{
            padding: '14px 16px',
            borderBottom: '1px solid #e2e8f0',
            fontSize: 13,
            fontWeight: 700,
            color: '#334155',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 8,
          }}>
            <span>کارمندان دپارتمان</span>
            {!loading && (
              <Badge variant="blue">{filteredEmployees.length}</Badge>
            )}
          </div>

          <div style={{ maxHeight: 420, overflowY: 'auto' }}>
            {loading ? (
              <div style={{ padding: 20 }}>
                <Skeleton h={14} style={{ marginBottom: 8 }} />
                <Skeleton h={14} w="70%" />
                <Skeleton h={14} w="85%" style={{ marginTop: 8 }} />
              </div>
            ) : filteredEmployees.length === 0 ? (
              <div style={{ padding: 24, fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>
                {search.trim() ? 'کارمندی با این نام یافت نشد' : 'کارمندی یافت نشد'}
              </div>
            ) : (
              filteredEmployees.map(username => {
                const active = selected === username;
                return (
                  <button
                    key={username}
                    type="button"
                    onClick={() => loadReport(username)}
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '12px 16px',
                      background: active ? '#EDF3FC' : 'transparent',
                      border: 'none',
                      borderBottom: '1px solid #f1f5f9',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      fontSize: 13,
                      color: active ? '#233A7A' : '#334155',
                      fontWeight: active ? 700 : 400,
                      textAlign: 'right',
                      transition: 'background 0.15s',
                    }}
                  >
                    {username}
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Report panel */}
        <div style={{ minWidth: 0 }}>
          {!selected ? (
            <EmptyState icon={<ArrowLeft size={44} weight="regular" />} title="یک کارمند را انتخاب کنید" subtitle="از لیست سمت راست کارمند مورد نظر را انتخاب کنید" />
          ) : reportLoading ? (
            <div style={{
              background: '#fff',
              borderRadius: 14,
              border: '1px solid #e2e8f0',
              padding: 48,
              textAlign: 'center',
            }}>
              <Spinner size={28} />
              <p style={{ marginTop: 14, fontSize: 13, color: '#64748b' }}>در حال بارگذاری گزارش...</p>
            </div>
          ) : parsedItems.length === 0 ? (
            <EmptyState icon={<Tray size={44} weight="regular" />} title="گزارشی برای این تاریخ ثبت نشده است" subtitle={`برای ${selected} در ${dateLabel} موردی یافت نشد`} />
          ) : (
            <div style={{
              background: '#fff',
              borderRadius: 14,
              border: '1px solid rgba(35,58,122,0.1)',
              overflow: 'hidden',
              boxShadow: '0 4px 16px rgba(35,58,122,0.08)',
            }}>
              <div style={{
                padding: '16px 20px',
                borderBottom: '1px solid #e2e8f0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 8,
              }}>
                <h3 style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', margin: 0 }}>
                  گزارش {selected}
                </h3>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  {approvalDisplay && (
                    <Badge variant={approval?.approved ? 'green' : approval?.needs_reapproval ? 'amber' : 'gray'}>
                      {approvalDisplay.label}
                      {approvalDisplay.when ? ` · ${approvalDisplay.when}` : ''}
                    </Badge>
                  )}
                  <Badge variant="gray">{parsedItems.length} مورد</Badge>
                  <Btn variant="secondary" size="sm" onClick={handlePrint}>🖨️ چاپ</Btn>
                  {!approval?.approved && (
                    <Btn size="sm" onClick={handleApprove} loading={approving}>
                      ✓ تایید گزارش
                    </Btn>
                  )}
                </div>
              </div>

              {approvalDisplay && (
                <div style={{
                  margin: '0 20px 16px',
                  background: approvalDisplay.bg,
                  border: `2px solid ${approvalDisplay.border}`,
                  borderRadius: 12,
                  padding: '14px 18px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 12,
                }}>
                  <span style={{
                    fontSize: 20,
                    lineHeight: 1,
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    background: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    border: `1px solid ${approvalDisplay.border}`,
                  }}>
                    {approvalDisplay.icon}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 800, color: approvalDisplay.color, marginBottom: 4 }}>
                      وضعیت گزارش: {approvalDisplay.label}
                    </div>
                    <div style={{ fontSize: 13, color: approvalDisplay.color, lineHeight: 1.6 }}>
                      {approvalDisplay.detail}
                    </div>
                  </div>
                </div>
              )}

              <div className="pj-manager-report-table-wrap">
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '48px minmax(120px, 1.1fr) minmax(160px, 1.8fr) minmax(88px, 0.7fr) minmax(120px, 1fr)',
                  padding: '12px 20px',
                  background: '#f8faff',
                  borderBottom: '1px solid #e2e8f0',
                  fontSize: 12,
                  fontWeight: 700,
                  color: '#64748b',
                  minWidth: 640,
                }}>
                  <span>#</span>
                  <span>عنوان</span>
                  <span>توضیحات</span>
                  <span>زمان تخمینی</span>
                  <span>جزئیات</span>
                </div>

                {parsedItems.map((item, i) => (
                  <div
                    key={item.id ?? i}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '48px minmax(120px, 1.1fr) minmax(160px, 1.8fr) minmax(88px, 0.7fr) minmax(120px, 1fr)',
                      padding: '14px 20px',
                      borderBottom: '1px solid #f1f5f9',
                      fontSize: 13,
                      color: '#334155',
                      background: i % 2 === 0 ? '#fff' : '#fafbff',
                      alignItems: 'start',
                      gap: 8,
                      minWidth: 640,
                    }}
                  >
                    <span style={{ color: '#94a3b8', fontWeight: 600 }}>{i + 1}</span>
                    <span style={{ fontWeight: 700, color: '#0f172a', lineHeight: 1.6, wordBreak: 'break-word' }}>
                      {item.title || '—'}
                    </span>
                    <span style={{ lineHeight: 1.7, color: '#475569', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {item.description || '—'}
                    </span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#233A7A' }}>
                      {formatEstimatedMinutes(item.estimatedTime ?? DEFAULT_ESTIMATED_TIME)}
                    </span>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' }}>
                      {item.companyName && (
                        <Badge variant="blue">شرکت: {companyLabel(item.companyName)}</Badge>
                      )}
                      {item.invoiceId && (
                        <Badge variant="amber">{invoiceCostLabel(item)}</Badge>
                      )}
                      {!item.companyName && !item.invoiceId && (
                        <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
